import { useState } from "react";
import {
  LucideIcon,
  CreditCard,
  ShoppingBag,
  Users,
  Store,
  TrendingUp,
  Clock,
  MapPin,
  Smartphone,
  Hotel,
  Package,
  Utensils,
  Pill,
} from "lucide-react";
import { Layout } from "../../components/layouts/DashboardLayout";

const StatCard = ({
  icon: Icon,
  label,
  value,
  sublabel,
  color = "blue",
  trend,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  sublabel?: string;
  color?: string;
  trend?: { value: number; isPositive: boolean };
}) => {
  const colorClasses: Record<string, string> = {
    blue: "border-blue-200 bg-blue-50 text-blue-600",
    green: "border-green-200 bg-green-50 text-green-600",
    red: "border-red-200 bg-red-50 text-red-600",
    orange: "border-orange-200 bg-orange-50 text-orange-600",
    purple: "border-purple-200 bg-purple-50 text-purple-600",
    indigo: "border-indigo-200 bg-indigo-50 text-indigo-600",
    pink: "border-pink-200 bg-pink-50 text-pink-600",
  };

  return (
    <div
      className={`p-4 rounded-lg border-2 ${colorClasses[color] || colorClasses.blue} hover:shadow-lg transition-shadow`}
    >
      <div className="flex justify-between items-start">
        <div className="flex gap-3 items-center">
          <Icon className="w-8 h-8" />
          <div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-sm font-medium">{label}</p>
            {sublabel && <p className="text-xs opacity-70">{sublabel}</p>}
          </div>
        </div>
        {trend && (
          <div
            className={`text-xs font-semibold px-2 py-1 rounded-full ${
              trend.isPositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}
          >
            {trend.isPositive ? "↑" : "↓"} {Math.abs(trend.value)}%
          </div>
        )}
      </div>
    </div>
  );
};

const SectionTitle = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div className="mb-4">
    <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
  </div>
);

export default function DashboardPage() {
  const [loading, setLoading] = useState(false);

  // Données simulées - À remplacer par tes vraies données API
  const stats = {
    global: {
      totalUsers: "45.2K",
      totalTransactions: "128.5K",
      totalRevenue: "2.4M",
      activeMerchants: "156",
      avgTransaction: "18.7K",
      successRate: "98.5%",
    },
    iziLifePay: {
      transfers: "32.4K",
      volume: "1.8M",
      users: "28.7K",
    },
    easyLifeMarket: {
      deliveries: "15.2K",
      restaurants: "45",
      hotels: "23",
      pharmacies: "18",
      articles: "89",
    },
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 mx-auto max-w-6xl">
          <div className="space-y-6 animate-pulse">
            <div className="w-64 h-8 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-2 space-y-6 max-w-7xl">
        {/* En-tête */}

        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vue d'ensemble Kafintech Group</h1>
          <p className="mt-1 text-gray-600">Statistiques consolidées de toutes vos solutions</p>
        </div>

        {/* KPIs Globaux */}
        <div>
          <SectionTitle title="Indicateurs Globaux" subtitle="Toutes solutions confondues" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <StatCard
              icon={Users}
              label="Utilisateurs actifs"
              value={stats.global.totalUsers}
              sublabel="+12% ce mois"
              color="blue"
              trend={{ value: 12, isPositive: true }}
            />
            <StatCard
              icon={CreditCard}
              label="Transactions"
              value={stats.global.totalTransactions}
              sublabel="Volume total"
              color="green"
            />
            <StatCard
              icon={TrendingUp}
              label="Volume financier"
              value={`${stats.global.totalRevenue}M FCFA`}
              sublabel="+8.3% vs mois dernier"
              color="purple"
              trend={{ value: 8.3, isPositive: true }}
            />
            <StatCard
              icon={Store}
              label="Marchands actifs"
              value={stats.global.activeMerchants}
              sublabel="Commerces partenaires"
              color="orange"
            />
            <StatCard
              icon={Clock}
              label="Taux de réussite"
              value={stats.global.successRate}
              sublabel="Transactions"
              color="green"
            />
            <StatCard
              icon={Package}
              label="Panier moyen"
              value={`${stats.global.avgTransaction} FCFA`}
              sublabel="Toutes transactions"
              color="indigo"
            />
          </div>
        </div>

        {/* Section IZI Life Pay */}
        <div>
          <SectionTitle title="IZI Life Pay - Transfert d'argent" subtitle="Statistiques des transactions mobiles" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatCard
              icon={Smartphone}
              label="Transferts effectués"
              value={stats.iziLifePay.transfers}
              sublabel="Ce mois"
              color="blue"
            />
            <StatCard
              icon={TrendingUp}
              label="Volume transféré"
              value={`${stats.iziLifePay.volume}M FCFA`}
              sublabel="+15% vs mois dernier"
              color="green"
              trend={{ value: 15, isPositive: true }}
            />
            <StatCard
              icon={Users}
              label="Utilisateurs actifs"
              value={stats.iziLifePay.users}
              sublabel="Sur IZI Life Pay"
              color="purple"
            />
          </div>
        </div>

        {/* Section Easy Life Market */}
        <div>
          <SectionTitle title="Easy Life Market - Livraison & Réservations" subtitle="Activité de la plateforme" />

          {/* Sous-section Livraisons */}
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-700 mb-3">Livraisons</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <StatCard
                icon={Package}
                label="Livraisons totales"
                value={stats.easyLifeMarket.deliveries}
                sublabel="Ce mois"
                color="orange"
              />
              <StatCard
                icon={Utensils}
                label="Restaurants"
                value={stats.easyLifeMarket.restaurants}
                sublabel="Partenaires"
                color="red"
              />
              <StatCard
                icon={Pill}
                label="Pharmacies"
                value={stats.easyLifeMarket.pharmacies}
                sublabel="Partenaires"
                color="green"
              />
              <StatCard
                icon={ShoppingBag}
                label="Articles"
                value={stats.easyLifeMarket.articles}
                sublabel="En vente"
                color="purple"
              />
            </div>
          </div>

          {/* Sous-section Hôtels */}
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">Hébergement</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <StatCard
                icon={Hotel}
                label="Hôtels partenaires"
                value={stats.easyLifeMarket.hotels}
                sublabel="Établissements"
                color="indigo"
              />
              <StatCard icon={MapPin} label="Villes couvertes" value="8" sublabel="À travers le pays" color="pink" />
              <StatCard icon={Users} label="Réservations" value="2.4K" sublabel="Ce mois" color="blue" />
            </div>
          </div>
        </div>

        {/* Section Agrégateur de paiement */}
        <div>
          <SectionTitle title="Agrégateur de paiement" subtitle="Intégrations et transactions" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <StatCard
              icon={Store}
              label="Entreprises intégrées"
              value="32"
              sublabel="+5 ce mois"
              color="green"
              trend={{ value: 18, isPositive: true }}
            />
            <StatCard
              icon={CreditCard}
              label="Moyens de paiement"
              value="8"
              sublabel="Mobile money, carte..."
              color="blue"
            />
            <StatCard
              icon={TrendingUp}
              label="Volume traité"
              value="850M FCFA"
              sublabel="Ce trimestre"
              color="purple"
            />
            <StatCard icon={Clock} label="Temps moyen" value="2.5s" sublabel="Par transaction" color="orange" />
          </div>
        </div>

        {/* Section Carte de performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performances */}
          <div className="p-4 bg-white rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Top Performances</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <span className="font-medium">IZI Life Pay</span>
                <span className="text-green-600">+32% ce mois</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <span className="font-medium">Easy Life Market</span>
                <span className="text-green-600">+28% ce mois</span>
              </div>
              <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                <span className="font-medium">Agrégateur</span>
                <span className="text-green-600">+45% ce mois</span>
              </div>
            </div>
          </div>

          {/* Activité récente */}
          <div className="p-4 bg-white rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Activité récente</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-gray-600">Nouvel hôtel partenaire à Lomé</span>
                <span className="text-gray-400 ml-auto">Il y a 2h</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-600">+500 utilisateurs IZI Life Pay</span>
                <span className="text-gray-400 ml-auto">Il y a 5h</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-600">Nouvelle intégration API</span>
                <span className="text-gray-400 ml-auto">Il y a 1j</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
